import { type ApolloClient, gql } from '@apollo/client/core';
import type { AnnounceDetail, Mutation, SaveAnnounceDetailMutationInput } from '../../../generated/api.graphql';
import { thawApolloResult } from '../../../util';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as AnnounceDetailInput from './AnnounceDetailInput';
import { ANNOUNCE_DETAIL_FIELDS } from './fragments';

/**
 * The AppSchema field is `saveAnnounceDetail`, not the legacy `createAnnounceDetail`, but the
 * *operation* name stays `SaveAnnounce`: e2e specs intercept on it.
 */
export const SAVE_ANNOUNCE_DETAIL_MUTATION = gql`
  mutation SaveAnnounce($input: SaveAnnounceDetailMutationInput!) {
    saveAnnounceDetail(input: $input) {
      success
      announceDetail {
        ...AnnounceDetailFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${ANNOUNCE_DETAIL_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/**
 * Returns the payload's announce detail unmapped. The admin store spreads it straight into
 * `selectedEvent.announceDetail`, so introducing a domain mapper here would change the shape
 * the whole announce modal reads — a separate story.
 *
 * Unmapped but **thawed**: with no `fromGql` rebuilding it, the frozen Apollo graph would
 * otherwise reach the announce form as-is. The modal re-runs `doReset` against this result
 * after a save, so it lands in exactly the vee-validate path that `EventDetail` had to be
 * fixed for — the read cutover alone would have left saving an event with artists broken.
 */
export const saveAnnounceDetail = async (
  client: ApolloClient<Mutation>,
  params: AnnounceDetailInput.AnnounceDetailInput,
): Promise<VPResult<AnnounceDetail, VPErrors.VPError[]>> => {
  const input: SaveAnnounceDetailMutationInput = { announceDetail: AnnounceDetailInput.toGqlInput(params) };

  const { data } = await client.mutate<Mutation>({ mutation: SAVE_ANNOUNCE_DETAIL_MUTATION, variables: { input } });

  const payload = data?.saveAnnounceDetail;

  if (!payload) {
    throw new Error('SaveAnnounce mutation returned no data');
  }

  if (payload.success) {
    if (!payload.announceDetail) {
      throw new Error('SaveAnnounce reported success without an announce detail');
    }

    return { success: true, data: thawApolloResult(payload.announceDetail) };
  }

  return { success: false, errors: VPErrors.fromGqlCollection(payload.errors ?? []) };
};
